import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { imageUrl } from "@/lib/images";

const inputCls = "field-input";
const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const COURSE_EMPTY = {
  title_en: "", title_ar: "", short_description_en: "", short_description_ar: "",
  description_en: "", description_ar: "", category: "", delivery_method: "online",
  duration: "", start_date: "", end_date: "", schedule: "", trainer: "", language: "",
  location: "", fee: "", discount_price: "", currency: "GBP", featured_image: "",
  objectives_en: "", objectives_ar: "", audience_en: "", audience_ar: "",
  prerequisites_en: "", prerequisites_ar: "", outline_en: "", outline_ar: "",
  outcomes_en: "", outcomes_ar: "", seats: "", registration_deadline: "",
  status: "draft", featured: false,
};

const LIST_FIELDS = ["objectives_en", "objectives_ar", "outline_en", "outline_ar", "outcomes_en", "outcomes_ar"];

export function AdminCourses() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, featured_image: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const load = () => api.get("/admin/courses").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    const form = { ...COURSE_EMPTY, ...c };
    LIST_FIELDS.forEach((k) => (form[k] = toLines(c[k])));
    form.fee = c.fee ?? "";
    form.discount_price = c.discount_price ?? "";
    form.seats = c.seats ?? "";
    setEditing(form);
  };

  const save = async () => {
    const payload = { ...editing };
    LIST_FIELDS.forEach((k) => (payload[k] = fromLines(payload[k])));
    payload.fee = payload.fee === "" ? null : parseFloat(payload.fee);
    payload.discount_price = payload.discount_price === "" ? null : parseFloat(payload.discount_price);
    payload.seats = payload.seats === "" ? null : parseInt(payload.seats, 10);
    try {
      if (payload.id) await api.patch(`/admin/courses/${payload.id}`, payload);
      else await api.post("/admin/courses", payload);
      toast.success("Course saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div data-testid="admin-courses">
      <div className="mb-5 flex justify-end">
        <button onClick={() => setEditing({ ...COURSE_EMPTY })} data-testid="course-add-button"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover">
          <Plus className="h-4 w-4" /> New Course
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[760px] text-sm" data-testid="admin-courses-table">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Title (EN)", "Category", "Delivery", "Dates", "Fee", "Status", "Featured", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-semibold text-navy">{c.title_en}</td>
                <td className="px-4 py-3 text-slate-600">{c.category}</td>
                <td className="px-4 py-3 text-slate-600">{c.delivery_method}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{c.start_date}</td>
                <td className="px-4 py-3 text-slate-600">{c.fee ? `${c.currency} ${c.fee}` : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${c.status === "published" ? "bg-emerald-100 text-emerald-800" : c.status === "archived" ? "bg-slate-200 text-slate-600" : "bg-amber-100 text-amber-800"}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.featured ? "★" : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(c)} data-testid={`course-edit-${c.id}`} className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(c.id)} data-testid={`course-delete-${c.id}`} className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No courses yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setEditing(null)} data-testid="course-editor-modal">
          <div className="mx-auto my-8 max-w-4xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{editing.id ? "Edit Course" : "New Course"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="field-label">Title (EN)</label><input className={inputCls} value={editing.title_en} onChange={set("title_en")} data-testid="course-form-title-en" /></div>
              <div><label className="field-label">Title (AR)</label><input dir="rtl" className={inputCls} value={editing.title_ar} onChange={set("title_ar")} data-testid="course-form-title-ar" /></div>
              <div><label className="field-label">Short description (EN)</label><textarea rows={2} className={inputCls} value={editing.short_description_en} onChange={set("short_description_en")} /></div>
              <div><label className="field-label">Short description (AR)</label><textarea dir="rtl" rows={2} className={inputCls} value={editing.short_description_ar} onChange={set("short_description_ar")} /></div>
              <div><label className="field-label">Full description (EN)</label><textarea rows={4} className={inputCls} value={editing.description_en} onChange={set("description_en")} /></div>
              <div><label className="field-label">Full description (AR)</label><textarea dir="rtl" rows={4} className={inputCls} value={editing.description_ar} onChange={set("description_ar")} /></div>
              <div><label className="field-label">Category</label><input className={inputCls} value={editing.category} onChange={set("category")} data-testid="course-form-category" /></div>
              <div><label className="field-label">Delivery method</label>
                <select className={inputCls} value={editing.delivery_method} onChange={set("delivery_method")}>
                  {["online", "in-person", "hybrid", "on-site"].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><label className="field-label">Duration</label><input className={inputCls} value={editing.duration} onChange={set("duration")} /></div>
              <div><label className="field-label">Schedule</label><input className={inputCls} value={editing.schedule} onChange={set("schedule")} /></div>
              <div><label className="field-label">Start date</label><input type="date" className={inputCls} value={editing.start_date} onChange={set("start_date")} /></div>
              <div><label className="field-label">End date</label><input type="date" className={inputCls} value={editing.end_date} onChange={set("end_date")} /></div>
              <div><label className="field-label">Trainer</label><input className={inputCls} value={editing.trainer} onChange={set("trainer")} /></div>
              <div><label className="field-label">Language</label><input className={inputCls} value={editing.language} onChange={set("language")} /></div>
              <div><label className="field-label">Location</label><input className={inputCls} value={editing.location} onChange={set("location")} /></div>
              <div><label className="field-label">Registration deadline</label><input type="date" className={inputCls} value={editing.registration_deadline} onChange={set("registration_deadline")} /></div>
              <div><label className="field-label">Fee</label><input type="number" min="0" step="0.01" className={inputCls} value={editing.fee} onChange={set("fee")} data-testid="course-form-fee" /></div>
              <div><label className="field-label">Discounted price (optional)</label><input type="number" min="0" step="0.01" className={inputCls} value={editing.discount_price} onChange={set("discount_price")} data-testid="course-form-discount-price" /></div>
              <div><label className="field-label">Currency</label><input className={inputCls} value={editing.currency} onChange={set("currency")} /></div>
              <div><label className="field-label">Available seats</label><input type="number" min="0" className={inputCls} value={editing.seats} onChange={set("seats")} /></div>
              <div className="sm:col-span-2">
                <label className="field-label">Featured image</label>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="course-upload-image-button"
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-navy-800 disabled:opacity-60">
                    <UploadCloud className="h-4 w-4" /> {uploading ? "Uploading..." : "Upload Image"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="course-image-file-input"
                    onChange={(e) => uploadImage(e.target.files[0])} />
                  <input className={`${inputCls} min-w-52 flex-1`} value={editing.featured_image} onChange={set("featured_image")} placeholder="...or paste an image URL" data-testid="course-form-image" />
                </div>
                {editing.featured_image && (
                  <img src={imageUrl(editing.featured_image)} alt="Course preview" data-testid="course-image-preview"
                    className="mt-3 h-32 w-56 rounded-md border border-slate-200 object-cover" />
                )}
              </div>
              <div><label className="field-label">Objectives (EN) — one per line</label><textarea rows={3} className={inputCls} value={editing.objectives_en} onChange={set("objectives_en")} /></div>
              <div><label className="field-label">Objectives (AR)</label><textarea dir="rtl" rows={3} className={inputCls} value={editing.objectives_ar} onChange={set("objectives_ar")} /></div>
              <div><label className="field-label">Outline (EN)</label><textarea rows={3} className={inputCls} value={editing.outline_en} onChange={set("outline_en")} /></div>
              <div><label className="field-label">Outline (AR)</label><textarea dir="rtl" rows={3} className={inputCls} value={editing.outline_ar} onChange={set("outline_ar")} /></div>
              <div><label className="field-label">Outcomes (EN)</label><textarea rows={3} className={inputCls} value={editing.outcomes_en} onChange={set("outcomes_en")} /></div>
              <div><label className="field-label">Outcomes (AR)</label><textarea dir="rtl" rows={3} className={inputCls} value={editing.outcomes_ar} onChange={set("outcomes_ar")} /></div>
              <div><label className="field-label">Target audience (EN)</label><input className={inputCls} value={editing.audience_en} onChange={set("audience_en")} /></div>
              <div><label className="field-label">Target audience (AR)</label><input dir="rtl" className={inputCls} value={editing.audience_ar} onChange={set("audience_ar")} /></div>
              <div><label className="field-label">Prerequisites (EN)</label><input className={inputCls} value={editing.prerequisites_en} onChange={set("prerequisites_en")} /></div>
              <div><label className="field-label">Prerequisites (AR)</label><input dir="rtl" className={inputCls} value={editing.prerequisites_ar} onChange={set("prerequisites_ar")} /></div>
              <div>
                <label className="field-label">Status</label>
                <select className={inputCls} value={editing.status} onChange={set("status")} data-testid="course-form-status">
                  <option value="draft">Draft</option><option value="published">Published</option>
                  <option value="unpublished">Unpublished</option><option value="archived">Archived</option>
                </select>
              </div>
              <label className="flex items-center gap-2 self-end text-sm text-slate-700">
                <input type="checkbox" checked={editing.featured} onChange={set("featured")} className="h-4 w-4 accent-[#C5A059]" data-testid="course-form-featured" /> Featured on homepage
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} data-testid="course-form-save-button" className="rounded-full bg-gold px-8 py-3 text-sm font-bold text-navy hover:bg-gold-hover">Save Course</button>
              <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-8 py-3 text-sm font-semibold text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const INSIGHT_EMPTY = {
  title_en: "", title_ar: "", summary_en: "", summary_ar: "", content_en: "", content_ar: "",
  featured_image: "", category: "", author: "", publish_date: "",
  seo_title: "", seo_description: "", keywords: "", status: "draft", featured: false,
};

export function AdminInsights() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/insights").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/admin/insights/${editing.id}`, editing);
      else await api.post("/admin/insights", editing);
      toast.success("Article saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/insights/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div data-testid="admin-insights">
      <div className="mb-5 flex justify-end">
        <button onClick={() => setEditing({ ...INSIGHT_EMPTY })} data-testid="insight-add-button"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover">
          <Plus className="h-4 w-4" /> New Article
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-sm" data-testid="admin-insights-table">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Title (EN)", "Category", "Author", "Date", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-semibold text-navy">{a.title_en}</td>
                <td className="px-4 py-3 text-slate-600">{a.category}</td>
                <td className="px-4 py-3 text-slate-600">{a.author}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{a.publish_date}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${a.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...INSIGHT_EMPTY, ...a })} data-testid={`insight-edit-${a.id}`} className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(a.id)} data-testid={`insight-delete-${a.id}`} className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No articles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setEditing(null)} data-testid="insight-editor-modal">
          <div className="mx-auto my-8 max-w-4xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{editing.id ? "Edit Article" : "New Article"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="field-label">Title (EN)</label><input className={inputCls} value={editing.title_en} onChange={set("title_en")} data-testid="insight-form-title-en" /></div>
              <div><label className="field-label">Title (AR)</label><input dir="rtl" className={inputCls} value={editing.title_ar} onChange={set("title_ar")} data-testid="insight-form-title-ar" /></div>
              <div><label className="field-label">Summary (EN)</label><textarea rows={2} className={inputCls} value={editing.summary_en} onChange={set("summary_en")} /></div>
              <div><label className="field-label">Summary (AR)</label><textarea dir="rtl" rows={2} className={inputCls} value={editing.summary_ar} onChange={set("summary_ar")} /></div>
              <div><label className="field-label">Content (EN)</label><textarea rows={7} className={inputCls} value={editing.content_en} onChange={set("content_en")} data-testid="insight-form-content-en" /></div>
              <div><label className="field-label">Content (AR)</label><textarea dir="rtl" rows={7} className={inputCls} value={editing.content_ar} onChange={set("content_ar")} /></div>
              <div><label className="field-label">Category</label><input className={inputCls} value={editing.category} onChange={set("category")} /></div>
              <div><label className="field-label">Author</label><input className={inputCls} value={editing.author} onChange={set("author")} /></div>
              <div><label className="field-label">Publish date</label><input type="date" className={inputCls} value={editing.publish_date} onChange={set("publish_date")} /></div>
              <div><label className="field-label">Featured image URL</label><input className={inputCls} value={editing.featured_image} onChange={set("featured_image")} /></div>
              <div><label className="field-label">SEO title</label><input className={inputCls} value={editing.seo_title} onChange={set("seo_title")} /></div>
              <div><label className="field-label">SEO description</label><input className={inputCls} value={editing.seo_description} onChange={set("seo_description")} /></div>
              <div><label className="field-label">Keywords</label><input className={inputCls} value={editing.keywords} onChange={set("keywords")} /></div>
              <div>
                <label className="field-label">Status</label>
                <select className={inputCls} value={editing.status} onChange={set("status")} data-testid="insight-form-status">
                  <option value="draft">Draft</option><option value="published">Published</option>
                </select>
              </div>
              <label className="flex items-center gap-2 self-end text-sm text-slate-700">
                <input type="checkbox" checked={editing.featured} onChange={set("featured")} className="h-4 w-4 accent-[#C5A059]" /> Featured on homepage
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} data-testid="insight-form-save-button" className="rounded-full bg-gold px-8 py-3 text-sm font-bold text-navy hover:bg-gold-hover">Save Article</button>
              <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-8 py-3 text-sm font-semibold text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const VIDEO_EMPTY = {
  youtube_url: "", title_en: "", title_ar: "", description_en: "", description_ar: "",
  thumbnail: "", category: "", language: "", publish_date: "", speaker: "", status: "draft", featured: false,
};

export function AdminVideos() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/videos").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.patch(`/admin/videos/${editing.id}`, editing);
      else await api.post("/admin/videos", editing);
      toast.success("Video saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div data-testid="admin-videos">
      <div className="mb-5 flex justify-end">
        <button onClick={() => setEditing({ ...VIDEO_EMPTY })} data-testid="video-add-button"
          className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-bold text-navy hover:bg-gold-hover">
          <Plus className="h-4 w-4" /> New Video
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-sm" data-testid="admin-videos-table">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Title (EN)", "Category", "Speaker", "Date", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-semibold text-navy">{v.title_en}</td>
                <td className="px-4 py-3 text-slate-600">{v.category}</td>
                <td className="px-4 py-3 text-slate-600">{v.speaker}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{v.publish_date}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${v.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{v.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing({ ...VIDEO_EMPTY, ...v })} data-testid={`video-edit-${v.id}`} className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(v.id)} data-testid={`video-delete-${v.id}`} className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100" aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No videos yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setEditing(null)} data-testid="video-editor-modal">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-display text-2xl font-semibold text-navy">{editing.id ? "Edit Video" : "New Video"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4">
              <div><label className="field-label">YouTube URL</label><input className={inputCls} value={editing.youtube_url} onChange={set("youtube_url")} placeholder="https://www.youtube.com/watch?v=..." data-testid="video-form-url" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><label className="field-label">Title (EN)</label><input className={inputCls} value={editing.title_en} onChange={set("title_en")} data-testid="video-form-title-en" /></div>
                <div><label className="field-label">Title (AR)</label><input dir="rtl" className={inputCls} value={editing.title_ar} onChange={set("title_ar")} /></div>
                <div><label className="field-label">Description (EN)</label><textarea rows={3} className={inputCls} value={editing.description_en} onChange={set("description_en")} /></div>
                <div><label className="field-label">Description (AR)</label><textarea dir="rtl" rows={3} className={inputCls} value={editing.description_ar} onChange={set("description_ar")} /></div>
                <div><label className="field-label">Thumbnail URL (optional)</label><input className={inputCls} value={editing.thumbnail} onChange={set("thumbnail")} /></div>
                <div><label className="field-label">Speaker / Trainer</label><input className={inputCls} value={editing.speaker} onChange={set("speaker")} /></div>
                <div><label className="field-label">Category</label><input className={inputCls} value={editing.category} onChange={set("category")} /></div>
                <div><label className="field-label">Language</label><input className={inputCls} value={editing.language} onChange={set("language")} /></div>
                <div><label className="field-label">Publish date</label><input type="date" className={inputCls} value={editing.publish_date} onChange={set("publish_date")} /></div>
                <div>
                  <label className="field-label">Status</label>
                  <select className={inputCls} value={editing.status} onChange={set("status")} data-testid="video-form-status">
                    <option value="draft">Draft</option><option value="published">Published</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editing.featured} onChange={set("featured")} className="h-4 w-4 accent-[#C5A059]" /> Featured on homepage
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} data-testid="video-form-save-button" className="rounded-full bg-gold px-8 py-3 text-sm font-bold text-navy hover:bg-gold-hover">Save Video</button>
              <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-8 py-3 text-sm font-semibold text-slate-600">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminSettings() {
  const [form, setForm] = useState({ payment_instructions_en: "", payment_instructions_ar: "", wise_instructions_en: "", wise_instructions_ar: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => { setForm(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!loaded) return <p className="text-sm text-slate-400">Loading...</p>;

  return (
    <div className="max-w-3xl rounded-lg border border-slate-200 bg-white p-8 shadow-sm" data-testid="admin-settings">
      <h2 className="font-display text-2xl font-semibold text-navy">Payment Instructions</h2>
      <p className="mt-1 text-sm text-slate-500">Wise bank transfer instructions shown after course registration and expert consultation booking. Add your Wise account details here — never hard-coded.</p>
      <div className="mt-6 grid gap-5">
        <div>
          <label className="field-label">Wise payment instructions (EN)</label>
          <textarea rows={5} className={inputCls} value={form.wise_instructions_en} onChange={(e) => setForm({ ...form, wise_instructions_en: e.target.value })} data-testid="settings-wise-en"
            placeholder="Wise account name, IBAN/account details, payment reference format..." />
        </div>
        <div>
          <label className="field-label">Wise payment instructions (AR)</label>
          <textarea dir="rtl" rows={5} className={inputCls} value={form.wise_instructions_ar} onChange={(e) => setForm({ ...form, wise_instructions_ar: e.target.value })} data-testid="settings-wise-ar" />
        </div>
        <div>
          <label className="field-label">General payment instructions (EN) — fallback</label>
          <textarea rows={4} className={inputCls} value={form.payment_instructions_en} onChange={(e) => setForm({ ...form, payment_instructions_en: e.target.value })} data-testid="settings-payment-en" />
        </div>
        <div>
          <label className="field-label">General payment instructions (AR) — fallback</label>
          <textarea dir="rtl" rows={4} className={inputCls} value={form.payment_instructions_ar} onChange={(e) => setForm({ ...form, payment_instructions_ar: e.target.value })} data-testid="settings-payment-ar" />
        </div>
        <div>
          <button onClick={save} data-testid="settings-save-button" className="rounded-full bg-gold px-8 py-3 text-sm font-bold text-navy hover:bg-gold-hover">Save Settings</button>
        </div>
      </div>
    </div>
  );
}
